import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import {
  AddCircleOutline,
  ArrowDownward,
  ArrowUpward,
  DeleteOutline,
} from "@mui/icons-material";

const STEPS = ["Objetivo", "Experiência", "Perguntas", "Configuração", "Revisão"];

const AUDIENCE_LABELS = {
  users: "Time / vendedor",
  clients: "Cliente",
  leads: "Lead",
  mixed: "Misto",
};

const TEMPLATE_GROUPS = [
  {
    key: "internal",
    title: "Para o time (interno)",
    description:
      "Respondida pelo vendedor ou operador na plataforma. Ideal para automação ao encerrar oportunidades.",
    match: (template) => isInternalSurveyTemplate(template),
  },
  {
    key: "external",
    title: "Para o cliente",
    description: "Enviada ao cliente após atendimento, compra ou pesquisa de relacionamento.",
    match: (template) => !isInternalSurveyTemplate(template),
  },
];

function groupSurveyTemplates(templates = []) {
  return TEMPLATE_GROUPS.map((group) => ({
    ...group,
    templates: templates.filter((template) => group.match(template)),
  })).filter((group) => group.templates.length > 0);
}

const INTERNAL_TEMPLATE_CODES = new Set(["lost_sale_reason"]);

function isInternalSurveyTemplate(template) {
  return (
    template?.audienceType === "users" ||
    INTERNAL_TEMPLATE_CODES.has(String(template?.code || ""))
  );
}

function buildTemplateDefaults(template) {
  if (!template) return {};
  if (!isInternalSurveyTemplate(template)) {
    return {
      thankYouTitle: "Obrigado pela sua resposta",
      thankYouMessage: "Sua opinião foi registrada com sucesso.",
      settings: { showProgress: true, allowAnonymous: true, collectProfile: false },
    };
  }
  return {
    collectionName: "Vendas — registro interno",
    thankYouTitle: "Motivo registrado",
    thankYouMessage: "O registro foi salvo e ficará disponível para análise da gestão comercial.",
    settings: { showProgress: true, allowAnonymous: false, collectProfile: false },
  };
}

const QUESTION_PRESETS = [
  { type: "info", label: "Conteúdo informativo" },
  { type: "nps", label: "NPS 0-10" },
  { type: "rating_scale", label: "Escala 1-5" },
  { type: "emoji_scale", label: "Escala emoji" },
  { type: "single_choice", label: "Escolha única" },
  { type: "multiple_choice", label: "Múltipla escolha" },
  { type: "yes_no", label: "Sim / Não" },
  { type: "short_text", label: "Texto curto" },
  { type: "long_text", label: "Texto longo" },
];

function buildDefaultOptions(type) {
  if (type === "yes_no") {
    return [
      { value: "yes", label: "Sim", score: 1, position: 1 },
      { value: "no", label: "Não", score: 0, position: 2 },
    ];
  }
  if (type === "rating_scale" || type === "emoji_scale") {
    return [1, 2, 3, 4, 5].map((item) => ({
      value: String(item),
      label: String(item),
      score: item,
      position: item,
    }));
  }
  return [];
}

function createQuestion(type, index) {
  return {
    id: null,
    key: `question_${Date.now()}_${index}`,
    type,
    title: "",
    description: "",
    required: true,
    isPrimaryMetric: index === 0,
    position: index + 1,
    scaleMin: type === "nps" ? 0 : type === "rating_scale" || type === "emoji_scale" ? 1 : null,
    scaleMax: type === "nps" ? 10 : type === "rating_scale" || type === "emoji_scale" ? 5 : null,
    options: buildDefaultOptions(type),
  };
}

function normalizeSurvey(initialSurvey = {}, templates = []) {
  const sourceSurvey = initialSurvey ?? {};
  const templateId = sourceSurvey.templateId || null;
  const template = templates.find((item) => item.id === templateId) || null;
  const questions = Array.isArray(sourceSurvey.questions)
    ? sourceSurvey.questions
    : template?.questions || [];

  return {
    id: sourceSurvey.id || null,
    title: sourceSurvey.title || sourceSurvey.titulo || "",
    description: sourceSurvey.description || sourceSurvey.resumo || "",
    metricType: sourceSurvey.metricType || template?.metricType || "custom",
    audienceType: sourceSurvey.audienceType || template?.audienceType || "clients",
    status: sourceSurvey.status || "draft",
    collectionName: sourceSurvey.collectionName || "",
    estimatedMinutes: sourceSurvey.estimatedMinutes || template?.estimatedMinutes || 2,
    thankYouTitle: sourceSurvey.thankYouTitle || "Obrigado pela sua resposta",
    thankYouMessage:
      sourceSurvey.thankYouMessage || "Sua opinião foi registrada com sucesso.",
    settings: {
      showProgress: true,
      allowAnonymous: true,
      collectProfile: false,
      ...(sourceSurvey.settings || {}),
    },
    templateId,
    questions: questions.map((question, index) => ({
      id: question?.id || null,
      key: question?.key || `question_${index + 1}`,
      type: question?.type || "single_choice",
      title: question?.title || "",
      description: question?.description || "",
      required: Boolean(question?.required),
      isPrimaryMetric: Boolean(question?.isPrimaryMetric),
      position: Number(question?.position) || index + 1,
      scaleMin: question?.scaleMin ?? null,
      scaleMax: question?.scaleMax ?? null,
      options: Array.isArray(question?.options)
        ? question.options.map((option, optionIndex) => ({
            id: option?.id || null,
            value: option?.value ?? String(optionIndex + 1),
            label: option?.label || "",
            score: option?.score ?? null,
            position: Number(option?.position) || optionIndex + 1,
          }))
        : [],
    })),
  };
}

export default function SurveyBuilderDialog({
  open,
  templates = [],
  initialSurvey,
  onClose,
  onSubmit,
  loading = false,
}) {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(() => normalizeSurvey(initialSurvey, templates));

  useEffect(() => {
    if (open) {
      setStep(0);
      setForm(normalizeSurvey(initialSurvey, templates));
    }
  }, [open, initialSurvey, templates]);

  const selectedTemplate = useMemo(
    () => templates.find((item) => item.id === form.templateId) || null,
    [templates, form.templateId]
  );
  const groupedTemplates = useMemo(() => groupSurveyTemplates(templates), [templates]);
  const isInternalTemplateSelected = isInternalSurveyTemplate(selectedTemplate);
  const stepHint = useMemo(() => {
    if (step === 0 && !form.title.trim()) return "Defina um título para continuar.";
    if (step === 2 && !form.questions.length) return "Adicione ao menos uma pergunta para continuar.";
    if (
      step === 2 &&
      form.questions.some((question) => !String(question.title || "").trim())
    ) {
      return "Preencha o enunciado das perguntas para manter a pesquisa clara.";
    }
    return "";
  }, [step, form.title, form.questions]);

  const canAdvance = useMemo(() => {
    if (step === 0) return Boolean(form.title.trim());
    if (step === 2) {
      return (
        form.questions.length > 0 &&
        form.questions.every((question) => String(question.title || "").trim())
      );
    }
    return true;
  }, [step, form.title, form.questions]);

  const updateQuestion = (index, updater) => {
    setForm((prev) => {
      const nextQuestions = [...prev.questions];
      nextQuestions[index] =
        typeof updater === "function" ? updater(nextQuestions[index]) : updater;
      nextQuestions.forEach((question, position) => {
        nextQuestions[position] = { ...question, position: position + 1 };
      });
      return { ...prev, questions: nextQuestions };
    });
  };

  const addQuestion = (type) => {
    setForm((prev) => ({
      ...prev,
      questions: [...prev.questions, createQuestion(type, prev.questions.length)],
    }));
  };

  const moveQuestion = (index, direction) => {
    setForm((prev) => {
      const target = index + direction;
      if (target < 0 || target >= prev.questions.length) return prev;
      const nextQuestions = [...prev.questions];
      [nextQuestions[index], nextQuestions[target]] = [nextQuestions[target], nextQuestions[index]];
      return {
        ...prev,
        questions: nextQuestions.map((question, position) => ({
          ...question,
          position: position + 1,
        })),
      };
    });
  };

  const removeQuestion = (index) => {
    setForm((prev) => {
      const removedWasPrimary = Boolean(prev.questions[index]?.isPrimaryMetric);
      let nextQuestions = prev.questions
        .filter((_, questionIndex) => questionIndex !== index)
        .map((question, position) => ({
          ...question,
          position: position + 1,
        }));
      if (removedWasPrimary && nextQuestions.length) {
        nextQuestions = nextQuestions.map((question, position) => ({
          ...question,
          isPrimaryMetric: position === 0,
        }));
      }
      return { ...prev, questions: nextQuestions };
    });
  };

  const addOption = (questionIndex) => {
    updateQuestion(questionIndex, (question) => ({
      ...question,
      options: [
        ...(question.options || []),
        {
          id: null,
          value: `${question.options.length + 1}`,
          label: "",
          score: null,
          position: question.options.length + 1,
        },
      ],
    }));
  };

  const updateOption = (questionIndex, optionIndex, updater) => {
    updateQuestion(questionIndex, (question) => {
      const nextOptions = [...(question.options || [])];
      nextOptions[optionIndex] =
        typeof updater === "function" ? updater(nextOptions[optionIndex]) : updater;
      return {
        ...question,
        options: nextOptions.map((option, index) => ({
          ...option,
          position: index + 1,
        })),
      };
    });
  };

  const removeOption = (questionIndex, optionIndex) => {
    updateQuestion(questionIndex, (question) => ({
      ...question,
      options: (question.options || []).filter((_, idx) => idx !== optionIndex),
    }));
  };

  const handleApplyTemplate = (templateId) => {
    if (!templateId) {
      setForm((prev) => ({ ...prev, templateId: null }));
      return;
    }
    const template = templates.find((item) => item.id === Number(templateId));
    if (!template) return;
    const defaults = buildTemplateDefaults(template);
    setForm((prev) => ({
      ...prev,
      templateId: template.id,
      metricType: template.metricType,
      audienceType: template.audienceType,
      estimatedMinutes: template.estimatedMinutes,
      title: prev.title || template.name,
      description: prev.description || template.description,
      collectionName: prev.collectionName || defaults.collectionName || "",
      thankYouTitle: defaults.thankYouTitle || prev.thankYouTitle,
      thankYouMessage: defaults.thankYouMessage || prev.thankYouMessage,
      settings: {
        ...(prev.settings || {}),
        ...(defaults.settings || {}),
      },
      questions: normalizeSurvey({ templateId: template.id, questions: template.questions }, templates)
        .questions,
    }));
  };

  const renderTemplateCard = (template) => (
    <Grid item xs={12} sm={6} md={4} key={template.id}>
      <Card
        variant="outlined"
        onClick={() => handleApplyTemplate(template.id)}
        sx={{
          height: "100%",
          cursor: "pointer",
          borderWidth: form.templateId === template.id ? 2 : 1,
          borderColor: form.templateId === template.id ? "primary.main" : "divider",
          bgcolor: form.templateId === template.id ? "action.selected" : "background.paper",
        }}
      >
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              {template.name}
            </Typography>
            <Chip
              size="small"
              label={template.metricType.toUpperCase()}
              color={isInternalSurveyTemplate(template) ? "secondary" : "primary"}
            />
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, minHeight: 40 }}>
            {template.description}
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
            <Chip
              size="small"
              variant="outlined"
              color={isInternalSurveyTemplate(template) ? "secondary" : "default"}
              label={AUDIENCE_LABELS[template.audienceType] || template.audienceType}
            />
            <Chip size="small" variant="outlined" label={`${template.questions.length} perguntas`} />
            <Chip size="small" variant="outlined" label={`${template.estimatedMinutes} min`} />
          </Stack>
        </CardContent>
      </Card>
    </Grid>
  );

  const handleSubmit = () => {
    onSubmit({
      ...form,
      questions: form.questions.map((question) => ({ ...question })),
    });
  };

  const canSubmit = form.title.trim() && form.questions.length > 0;
  const hasResponses =
    Number(initialSurvey?.summary?.startedCount || 0) > 0 ||
    Number(initialSurvey?.summary?.completedCount || 0) > 0;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle>{form.id ? "Editar pesquisa" : "Nova pesquisa"}</DialogTitle>
      <DialogContent dividers>
        <Stepper activeStep={step} sx={{ mb: 3, pt: 1 }}>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        {stepHint ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            {stepHint}
          </Alert>
        ) : null}
        {hasResponses ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Esta pesquisa já possui respostas. Você pode alterar status, textos e configurações; a
            estrutura das perguntas permanece preservada.
          </Alert>
        ) : null}

        {step === 0 && (
          <Grid container spacing={2}>
            {!form.id && templates.length ? (
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
                  Como deseja começar?
                </Typography>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} sm={6} md={4}>
                    <Card
                      variant="outlined"
                      onClick={() => handleApplyTemplate("")}
                      sx={{
                        height: "100%",
                        cursor: "pointer",
                        borderWidth: !form.templateId ? 2 : 1,
                        borderColor: !form.templateId ? "primary.main" : "divider",
                        bgcolor: !form.templateId ? "action.selected" : "background.paper",
                      }}
                    >
                      <CardContent>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                          Em branco
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          Monte a pesquisa do zero, pergunta por pergunta.
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                {groupedTemplates.map((group) => (
                  <Box key={group.key} sx={{ mt: 2.5 }}>
                    <Typography variant="subtitle2" fontWeight={700}>
                      {group.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                      {group.description}
                    </Typography>
                    <Grid container spacing={1.5}>
                      {group.templates.map((template) => renderTemplateCard(template))}
                    </Grid>
                  </Box>
                ))}

                {isInternalTemplateSelected ? (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    Pesquisa <strong>interna</strong>: será respondida pelo vendedor na plataforma.
                    Publique a pesquisa e vincule à regra de automação{" "}
                    <strong>Abrir pesquisa na tela</strong> (ex.: ao alterar status para Perdida).
                  </Alert>
                ) : null}
              </Grid>
            ) : null}
            <Grid item xs={12}>
              <Divider sx={{ my: form.id || !templates.length ? 0 : 1 }} />
            </Grid>
            <Grid item xs={12} md={form.id ? 8 : 12}>
              <TextField
                fullWidth
                label="Título da pesquisa"
                value={form.title}
                onChange={(event) => setForm((prev) => ({ ...prev, title: event.target.value }))}
              />
            </Grid>
            {form.id ? (
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Template base</InputLabel>
                  <Select
                    label="Template base"
                    value={form.templateId || ""}
                    onChange={(event) => handleApplyTemplate(event.target.value)}
                  >
                    <MenuItem value="">Sem template</MenuItem>
                    {templates.map((template) => (
                      <MenuItem key={template.id} value={template.id}>
                        {template.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            ) : null}
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={3}
                label="Objetivo / resumo"
                value={form.description}
                onChange={(event) =>
                  setForm((prev) => ({ ...prev, description: event.target.value }))
                }
              />
            </Grid>
            {selectedTemplate && (
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      <Chip label={selectedTemplate.metricType.toUpperCase()} color="primary" />
                      <Chip label={`${selectedTemplate.estimatedMinutes} min`} variant="outlined" />
                      {(selectedTemplate.tags || []).map((tag) => (
                        <Chip key={tag} label={tag} size="small" />
                      ))}
                    </Stack>
                    <Typography variant="body2" sx={{ mt: 2 }}>
                      {selectedTemplate.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>
        )}

        {step === 1 && (
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Métrica principal</InputLabel>
                <Select
                  label="Métrica principal"
                  value={form.metricType}
                  onChange={(event) =>
                    setForm((prev) => ({ ...prev, metricType: event.target.value }))
                  }
                >
                  <MenuItem value="nps">NPS</MenuItem>
                  <MenuItem value="csat">CSAT</MenuItem>
                  <MenuItem value="ces">CES</MenuItem>
                  <MenuItem value="custom">Custom</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Público</InputLabel>
                <Select
                  label="Público"
                  value={form.audienceType}
                  onChange={(event) =>
                    setForm((prev) => ({ ...prev, audienceType: event.target.value }))
                  }
                >
                  <MenuItem value="clients">Clientes (pesquisa externa)</MenuItem>
                  <MenuItem value="leads">Leads</MenuItem>
                  <MenuItem value="users">Usuários / vendedores (interno)</MenuItem>
                  <MenuItem value="mixed">Misto</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="number"
                label="Tempo estimado (min)"
                value={form.estimatedMinutes}
                onChange={(event) =>
                  setForm((prev) => ({
                    ...prev,
                    estimatedMinutes: Number(event.target.value) || 1,
                  }))
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Coleção / pasta"
                value={form.collectionName}
                onChange={(event) =>
                  setForm((prev) => ({ ...prev, collectionName: event.target.value }))
                }
              />
            </Grid>
          </Grid>
        )}

        {step === 2 && (
          <Stack spacing={2}>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {QUESTION_PRESETS.map((preset) => (
                <Button
                  key={preset.type}
                  variant="outlined"
                  startIcon={<AddCircleOutline />}
                  onClick={() => addQuestion(preset.type)}
                >
                  {preset.label}
                </Button>
              ))}
            </Stack>
            {!form.questions.length ? (
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2">Comece adicionando a primeira pergunta</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Escolha um tipo acima. Você pode reordenar, editar e remover perguntas depois.
                  </Typography>
                </CardContent>
              </Card>
            ) : null}

            {form.questions.map((question, index) => (
              <Card key={`${question.key}-${index}`} variant="outlined">
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Chip label={`#${index + 1}`} size="small" />
                      <Chip label={question.type} size="small" variant="outlined" />
                      {question.isPrimaryMetric && (
                        <Chip label="Métrica principal" size="small" color="primary" />
                      )}
                    </Stack>
                    <Stack direction="row" spacing={1}>
                      <IconButton onClick={() => moveQuestion(index, -1)} disabled={index === 0}>
                        <ArrowUpward fontSize="small" />
                      </IconButton>
                      <IconButton
                        onClick={() => moveQuestion(index, 1)}
                        disabled={index === form.questions.length - 1}
                      >
                        <ArrowDownward fontSize="small" />
                      </IconButton>
                      <IconButton onClick={() => removeQuestion(index)} color="error">
                        <DeleteOutline fontSize="small" />
                      </IconButton>
                    </Stack>
                  </Stack>

                  <Grid container spacing={2}>
                    <Grid item xs={12} md={8}>
                      <TextField
                        fullWidth
                        label="Pergunta"
                        value={question.title}
                        onChange={(event) =>
                          updateQuestion(index, {
                            ...question,
                            title: event.target.value,
                          })
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <FormControl fullWidth>
                        <InputLabel>Tipo</InputLabel>
                        <Select
                          label="Tipo"
                          value={question.type}
                          onChange={(event) =>
                            updateQuestion(index, {
                              ...question,
                              type: event.target.value,
                              options: buildDefaultOptions(event.target.value),
                            })
                          }
                        >
                          {QUESTION_PRESETS.map((preset) => (
                            <MenuItem key={preset.type} value={preset.type}>
                              {preset.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Ajuda / descrição"
                        value={question.description}
                        onChange={(event) =>
                          updateQuestion(index, {
                            ...question,
                            description: event.target.value,
                          })
                        }
                      />
                    </Grid>
                    {(question.type === "nps" ||
                      question.type === "rating_scale" ||
                      question.type === "emoji_scale") && (
                      <>
                        <Grid item xs={12} md={3}>
                          <TextField
                            fullWidth
                            type="number"
                            label="Escala mínima"
                            value={question.scaleMin ?? ""}
                            onChange={(event) =>
                              updateQuestion(index, {
                                ...question,
                                scaleMin: Number(event.target.value),
                              })
                            }
                          />
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <TextField
                            fullWidth
                            type="number"
                            label="Escala máxima"
                            value={question.scaleMax ?? ""}
                            onChange={(event) =>
                              updateQuestion(index, {
                                ...question,
                                scaleMax: Number(event.target.value),
                              })
                            }
                          />
                        </Grid>
                      </>
                    )}
                    <Grid item xs={12}>
                      <Stack direction="row" spacing={2}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={question.required}
                              onChange={(event) =>
                                updateQuestion(index, {
                                  ...question,
                                  required: event.target.checked,
                                })
                              }
                            />
                          }
                          label="Obrigatória"
                        />
                        <FormControlLabel
                          control={
                            <Switch
                              checked={question.isPrimaryMetric}
                              onChange={(event) =>
                                setForm((prev) => ({
                                  ...prev,
                                  questions: prev.questions.map((item, itemIndex) => ({
                                    ...item,
                                    isPrimaryMetric:
                                      itemIndex === index ? event.target.checked : false,
                                  })),
                                }))
                              }
                            />
                          }
                          label="Métrica principal"
                        />
                      </Stack>
                    </Grid>
                  </Grid>

                  {["single_choice", "multiple_choice", "yes_no", "rating_scale", "emoji_scale"].includes(
                    question.type
                  ) && (
                    <>
                      <Divider sx={{ my: 2 }} />
                      <Stack spacing={1}>
                        <Typography variant="subtitle2">Opções</Typography>
                        {(question.options || []).map((option, optionIndex) => (
                          <Grid container spacing={1} key={`${option.position}-${optionIndex}`}>
                            <Grid item xs={12} md={4}>
                              <TextField
                                fullWidth
                                label="Rótulo"
                                value={option.label}
                                onChange={(event) =>
                                  updateOption(index, optionIndex, {
                                    ...option,
                                    label: event.target.value,
                                  })
                                }
                              />
                            </Grid>
                            <Grid item xs={12} md={3}>
                              <TextField
                                fullWidth
                                label="Valor"
                                value={option.value ?? ""}
                                onChange={(event) =>
                                  updateOption(index, optionIndex, {
                                    ...option,
                                    value: event.target.value,
                                  })
                                }
                              />
                            </Grid>
                            <Grid item xs={12} md={3}>
                              <TextField
                                fullWidth
                                type="number"
                                label="Score"
                                value={option.score ?? ""}
                                onChange={(event) =>
                                  updateOption(index, optionIndex, {
                                    ...option,
                                    score:
                                      event.target.value === ""
                                        ? null
                                        : Number(event.target.value),
                                  })
                                }
                              />
                            </Grid>
                            <Grid item xs={12} md={2}>
                              <Button
                                fullWidth
                                color="error"
                                variant="outlined"
                                onClick={() => removeOption(index, optionIndex)}
                              >
                                Remover
                              </Button>
                            </Grid>
                          </Grid>
                        ))}
                        <Box>
                          <Button variant="text" onClick={() => addOption(index)}>
                            Adicionar opção
                          </Button>
                        </Box>
                      </Stack>
                    </>
                  )}
                </CardContent>
              </Card>
            ))}
          </Stack>
        )}

        {step === 3 && (
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  label="Status"
                  value={form.status}
                  onChange={(event) =>
                    setForm((prev) => ({ ...prev, status: event.target.value }))
                  }
                >
                  <MenuItem value="draft">Rascunho</MenuItem>
                  <MenuItem value="published">Publicada</MenuItem>
                  <MenuItem value="archived">Arquivada</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.settings.showProgress}
                    onChange={(event) =>
                      setForm((prev) => ({
                        ...prev,
                        settings: { ...prev.settings, showProgress: event.target.checked },
                      }))
                    }
                  />
                }
                label="Exibir progresso"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.settings.allowAnonymous}
                    onChange={(event) =>
                      setForm((prev) => ({
                        ...prev,
                        settings: { ...prev.settings, allowAnonymous: event.target.checked },
                      }))
                    }
                  />
                }
                label="Permitir anonimato"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.settings.collectProfile}
                    onChange={(event) =>
                      setForm((prev) => ({
                        ...prev,
                        settings: { ...prev.settings, collectProfile: event.target.checked },
                      }))
                    }
                  />
                }
                label="Coletar perfil"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Título de agradecimento"
                value={form.thankYouTitle}
                onChange={(event) =>
                  setForm((prev) => ({ ...prev, thankYouTitle: event.target.value }))
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={3}
                label="Mensagem final"
                value={form.thankYouMessage}
                onChange={(event) =>
                  setForm((prev) => ({ ...prev, thankYouMessage: event.target.value }))
                }
              />
            </Grid>
          </Grid>
        )}

        {step === 4 && (
          <Stack spacing={2}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6">{form.title || "Pesquisa sem título"}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {form.description || "Sem resumo informado."}
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap" useFlexGap>
                  <Chip label={form.metricType.toUpperCase()} color="primary" />
                  <Chip label={form.audienceType} variant="outlined" />
                  <Chip label={form.status} variant="outlined" />
                  <Chip label={`${form.questions.length} perguntas`} variant="outlined" />
                </Stack>
              </CardContent>
            </Card>

            {form.questions.map((question, index) => (
              <Card key={`${question.key}-${index}`} variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1">
                    {index + 1}. {question.title || "Pergunta sem título"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {question.description || question.type}
                  </Typography>
                  {!!question.options.length && (
                    <Stack direction="row" spacing={1} mt={2} flexWrap="wrap" useFlexGap>
                      {question.options.map((option) => (
                        <Chip
                          key={`${option.position}-${option.label}`}
                          label={`${option.label}${option.score != null ? ` (${option.score})` : ""}`}
                          size="small"
                        />
                      ))}
                    </Stack>
                  )}
                </CardContent>
              </Card>
            ))}
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>Cancelar</Button>
        <Button onClick={() => setStep((prev) => Math.max(0, prev - 1))} disabled={step === 0}>
          Voltar
        </Button>
        {step < STEPS.length - 1 ? (
          <Button
            variant="contained"
            disabled={!canAdvance}
            onClick={() => setStep((prev) => prev + 1)}
          >
            Próximo
          </Button>
        ) : (
          <Button variant="contained" disabled={!canSubmit || loading} onClick={handleSubmit}>
            {loading ? "Salvando..." : "Salvar pesquisa"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
